import { useCallback, useMemo, useState } from 'react';
import {
    useToast,
} from '@chakra-ui/react';
import { FiSave, FiX } from 'react-icons/fi';
import { saveQuoteRevision } from '../../api/revision-api';
import { getQuoteDiff, mapSnapshotToRows, calculateTotalFromRows, TotalType } from './utils';
import { QuoteTableEdit } from './QuoteTableEdit';
import { QuoteTableView } from './QuoteTableView';
import type { QuoteRevisionSnapshot } from '../../types/revision.type';
import type { RfxQuoteTable } from '../../types/quote.type';
import { Modal } from 'newui/components/ui/Modal';
import { Button } from 'newui/components/Button';

export interface UpdateQuoteModalProps {
    currentData: QuoteRevisionSnapshot;
    inviteId: number;
    isOpen: boolean;
    previousData: QuoteRevisionSnapshot;
    quote: RfxQuoteTable;
    onClose: () => void;
    onSaveSuccess: () => void;
}

interface EditableAnswerValues {
    [itemId: number]: {
        id?: number;
        quantity: string;
        rate: string;
    };
}

const buildInitialEditValues = (data: QuoteRevisionSnapshot): EditableAnswerValues => {
    const initial: EditableAnswerValues = {};
    data?.answers.forEach((ans) => {
        initial[ans.rfq_quote_item_id] = {
            id: ans.id,
            quantity: String(ans.answer.value.quantity),
            rate: String(ans.answer.value.rate),
        };
    });
    return initial;
};

const UpdateQuoteModal: React.FC<UpdateQuoteModalProps> = ({
    isOpen,
    onClose,
    inviteId,
    currentData,
    previousData,
    onSaveSuccess,
    quote,
}) => {
    const toast = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [editValues, setEditValues] = useState<EditableAnswerValues>(
        () => buildInitialEditValues(currentData)
    );

    const currentWithEdits = useMemo((): QuoteRevisionSnapshot => {
        return {
            ...currentData,
            answers: currentData?.answers.map((ans) => ({
                ...ans,
                answer: {
                    value: {
                        ...ans.answer.value,
                        quantity: editValues[ans.rfq_quote_item_id]?.quantity ?? ans.answer.value.quantity,
                        rate: editValues[ans.rfq_quote_item_id]?.rate ?? ans.answer.value.rate,
                    },
                },
            })),
        };
    }, [currentData, editValues]);

    const diffKeys = useMemo(
        () => (currentData && previousData ? getQuoteDiff(currentWithEdits, previousData) : new Set<string>()),
        [currentWithEdits, previousData, currentData]
    );

    const currentRows = useMemo(
        () => mapSnapshotToRows(currentWithEdits, quote.items),
        [currentWithEdits, quote.items]
    );

    const previousRows = useMemo(
        () => mapSnapshotToRows(previousData, quote.items),
        [previousData, quote.items]
    );

    const previousSubtotal = useMemo(
        () => calculateTotalFromRows(previousRows, TotalType.SubTotal),
        [previousRows]
    );

    const hasChanges = diffKeys.size > 0;

    const handleValueChange = useCallback(
        (itemId: number, field: 'quantity' | 'rate', value: string) => {
            setEditValues((prev) => ({
                ...prev,
                [itemId]: {
                    ...prev[itemId],
                    [field]: value,
                },
            }));
        },
        []
    );

    const handleSave = async () => {
        setIsLoading(true);
        try {
            const quoteAnswers: Record<string, unknown> = {};
            Object.entries(editValues).forEach(([itemId, vals]) => {
                quoteAnswers[itemId] = {
                    ...(vals.id != null && { id: vals.id }),
                    quantity: Number(vals.quantity),
                    rate: Number(vals.rate),
                };
            });
            await saveQuoteRevision(
                { inviteId: inviteId, quoteId: quote.id },
                { quote_answers: quoteAnswers }
            );
            toast({
                title: 'Revision saved',
                description: 'Your quote revision has been saved.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            onSaveSuccess();
            onClose();
        } catch {
            toast({
                title: 'Error',
                description: 'Failed to save quote revision.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setEditValues(buildInitialEditValues(currentData));
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            size="full"
            title="Update Quote"
            modalType='standard'
            iconType='edit'
            customFooter={<div className='flex justify-end gap-4'>
                <Button
                    btntype='secondary'
                    onClick={handleCancel}
                    disabled={isLoading}
                    data-testid="cancel-update-btn"
                >
                    <FiX /> <span className='pl-2'>Cancel Changes</span>
                </Button>
                <Button
                    onClick={handleSave}
                    isLoading={isLoading}
                    disabled={!hasChanges || isLoading}
                    data-testid="save-revision-btn"
                >
                    <FiSave /> <span className='pl-2'>Save Revision</span>
                </Button></div>}
        >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="mb-6">
                    <p className="mb-3 text-base font-semibold text-gray-700">
                        Current Revision (Editable) - {currentData.label}
                    </p>
                    <QuoteTableEdit
                        rows={currentRows}
                        editValues={editValues}
                        onValueChange={handleValueChange}
                        diffKeys={diffKeys}
                        previousSubtotal={previousSubtotal}
                        showUnitColumn
                        showSubTotal
                    />
                </div>

                <div>
                    <p className="mb-3 text-base font-semibold text-gray-500">
                        Previous Revision (Read-only) - {previousData.label}
                    </p>
                    <QuoteTableView
                        rows={previousRows}
                        showUnitColumn
                        muted
                        showSubTotal
                    />
                </div>
            </div>
        </Modal>
    );
};

export { UpdateQuoteModal };
